import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  useMediaFolders, useCreateFolder, useDeleteFolder, useUpdateFolder,
  useMoveFolder, useMoveImages,
} from '@/api/hooks/useMediaLibrary'
import { BASE, getAuthHeaders } from '@/api/client'
import {
  FolderOpen, Folder, Plus, Trash2, Pencil, X, Check, Loader2,
  CalendarPlus, ChevronRight, ChevronDown, Search, Download,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import type { MediaFolder } from '@/types'

interface FolderPanelProps {
  currentFolderId: string | null
  onNavigate: (folderId: string | null) => void
  onScheduleFolders?: (folders: Array<{ id: string; name: string; imageCount?: number }>) => void
}

function isDescendant(folders: MediaFolder[], folderId: string, targetId: string): boolean {
  let current = targetId
  while (current) {
    if (current === folderId) return true
    const folder = folders.find(f => f.id === current)
    if (!folder || !folder.parentId) break
    current = folder.parentId
  }
  return false
}

export function FolderPanel({ currentFolderId, onNavigate, onScheduleFolders }: FolderPanelProps) {
  const { t } = useLanguage()
  const { data: allFoldersRaw, isLoading } = useMediaFolders()
  const allFolders = allFoldersRaw?.filter(f => f.type !== 'cuts') || []
  const createFolder = useCreateFolder()
  const deleteFolder = useDeleteFolder()
  const updateFolder = useUpdateFolder()
  const moveFolder = useMoveFolder()
  const moveImages = useMoveImages()

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newTags, setNewTags] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [scheduleMode, setScheduleMode] = useState(false)
  const [scheduleSelected, setScheduleSelected] = useState<Set<string>>(new Set())

  // Search + tag filter
  const [searchQuery, setSearchQuery] = useState('')
  const [tagFilter, setTagFilter] = useState<string>('all')

  const rootFolders = allFolders.filter(f => !f.parentId)

  const getChildren = useCallback((parentId: string) => {
    return allFolders.filter(f => f.parentId === parentId)
  }, [allFolders])

  // All unique tags across folders
  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    allFolders.forEach(f => f.tags?.forEach(t => tagSet.add(t)))
    return Array.from(tagSet).sort()
  }, [allFolders])

  // Filtered folders
  const isFiltering = searchQuery || tagFilter !== 'all'

  const filteredFolders = useMemo(() => {
    if (!isFiltering) return []
    return allFolders.filter(f => {
      if (searchQuery && !f.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
      if (tagFilter !== 'all' && !(f.tags || []).includes(tagFilter)) return false
      return true
    })
  }, [allFolders, searchQuery, tagFilter, isFiltering])

  // Auto-expand ancestors when selecting a folder
  useEffect(() => {
    if (!currentFolderId) return
    const ancestors: string[] = []
    let id: string | null | undefined = currentFolderId
    while (id) {
      const folder = allFolders.find(f => f.id === id)
      if (!folder || !folder.parentId) break
      ancestors.push(folder.parentId)
      id = folder.parentId
    }
    if (ancestors.length > 0) {
      setExpanded(prev => {
        const next = new Set(prev)
        ancestors.forEach(a => next.add(a))
        return next
      })
    }
  }, [currentFolderId, allFolders])

  const toggleExpand = (folderId: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const handleCreate = () => {
    if (!newName.trim()) return
    const tags = newTags
      .split(/[,;\s]+/)
      .map(t => t.replace(/^#/, '').trim())
      .filter(Boolean)
    createFolder.mutate({
      name: newName.trim(),
      parentId: currentFolderId,
      tags: tags.length > 0 ? tags : undefined,
    }, {
      onSuccess: () => {
        toast.success(t('Pasta criada!'))
        setNewName('')
        setNewTags('')
        setShowCreate(false)
        if (currentFolderId) {
          setExpanded(prev => new Set(prev).add(currentFolderId))
        }
      },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleDelete = (e: React.MouseEvent, folderId: string, name: string) => {
    e.stopPropagation()
    const subs = allFolders.filter(f => f.parentId === folderId).length
    const msg = subs > 0
      ? `${t('Deletar pasta')} "${name}" ${t('e')} ${subs} ${t('subpastas')}?`
      : `${t('Deletar pasta')} "${name}"?`
    if (!confirm(msg)) return
    deleteFolder.mutate(folderId, {
      onSuccess: () => {
        toast.success(t('Pasta deletada'))
        if (currentFolderId === folderId) onNavigate(null)
      },
    })
  }

  const handleRename = (folderId: string) => {
    if (!renameValue.trim()) return
    updateFolder.mutate({ folderId, name: renameValue.trim() }, {
      onSuccess: () => { toast.success(t('Pasta renomeada')); setRenamingId(null) },
    })
  }

  const handleDrop = (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverId(null)

    const imageData = e.dataTransfer.getData('application/x-media-images')
    if (imageData) {
      try {
        const imageIds: string[] = JSON.parse(imageData)
        moveImages.mutate({ imageIds, targetFolderId }, {
          onSuccess: (data) => toast.success(`${data.moved} arquivo(s) movido(s)`),
          onError: () => toast.error(t('Erro ao mover arquivos')),
        })
      } catch { /* ignore */ }
      return
    }

    const folderData = e.dataTransfer.getData('application/x-media-folder')
    if (folderData && folderData !== targetFolderId) {
      if (isDescendant(allFolders, folderData, targetFolderId)) {
        toast.error(t('Nao pode mover para dentro de si mesma'))
        return
      }
      moveFolder.mutate({ folderId: folderData, parentId: targetFolderId }, {
        onSuccess: () => {
          toast.success(t('Pasta movida'))
          setExpanded(prev => new Set(prev).add(targetFolderId))
        },
        onError: () => toast.error(t('Erro ao mover pasta')),
      })
    }
  }

  const handleDragOver = (e: React.DragEvent, folderId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragOverId !== folderId) setDragOverId(folderId)
  }

  const getDescendantIds = useCallback((folderId: string): string[] => {
    const children = getChildren(folderId)
    const all: string[] = []
    for (const child of children) {
      all.push(child.id)
      all.push(...getDescendantIds(child.id))
    }
    return all
  }, [getChildren])

  const toggleScheduleSelect = (folderId: string) => {
    setScheduleSelected(prev => {
      const next = new Set(prev)
      const descendants = getDescendantIds(folderId)
      if (next.has(folderId)) {
        next.delete(folderId)
        descendants.forEach(d => next.delete(d))
      } else {
        next.add(folderId)
        descendants.forEach(d => next.add(d))
      }
      return next
    })
  }

  const exitScheduleMode = () => {
    setScheduleMode(false)
    setScheduleSelected(new Set())
  }

  const handleScheduleSelected = () => {
    if (!onScheduleFolders) return
    const selected = allFolders
      .filter(f => scheduleSelected.has(f.id) && f.images.length > 0)
      .map(f => ({ id: f.id, name: f.name, imageCount: f.images.length }))
    if (selected.length === 0) { toast.error(t('Selecione pastas com arquivos')); return }
    onScheduleFolders(selected)
    exitScheduleMode()
  }

  const handleRootDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverId(null)
    const folderData = e.dataTransfer.getData('application/x-media-folder')
    if (folderData) {
      const folder = allFolders.find(f => f.id === folderData)
      if (folder && folder.parentId) {
        moveFolder.mutate({ folderId: folderData, parentId: null }, {
          onSuccess: () => toast.success(t('Pasta movida para raiz')),
          onError: () => toast.error(t('Erro ao mover pasta')),
        })
      }
    }
  }

  const getParentPath = (folder: MediaFolder): string => {
    if (!folder.parentId) return ''
    const parent = allFolders.find(f => f.id === folder.parentId)
    return parent ? parent.name : ''
  }

  function renderTreeItem(folder: MediaFolder, depth: number) {
    const children = getChildren(folder.id)
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(folder.id)
    const isSelected = folder.id === currentFolderId
    const isDragOver = folder.id === dragOverId
    const isScheduleChecked = scheduleSelected.has(folder.id)

    return (
      <div key={folder.id}>
        <div
          className={cn(
            'group flex items-center h-8 cursor-pointer text-[12px] pr-2 transition-all duration-150 rounded-lg mx-1.5 my-0.5',
            isSelected && !scheduleMode && 'bg-primary/10 text-primary font-medium',
            isDragOver && 'bg-primary/15 ring-1 ring-inset ring-primary/40',
            scheduleMode && isScheduleChecked && 'bg-primary/10',
            !isSelected && !isDragOver && 'hover:bg-muted/60',
          )}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          draggable={!scheduleMode && !renamingId}
          onDragStart={(e) => {
            e.dataTransfer.setData('application/x-media-folder', folder.id)
            e.dataTransfer.effectAllowed = 'move'
          }}
          onDragOver={(e) => handleDragOver(e, folder.id)}
          onDragLeave={() => setDragOverId(null)}
          onDrop={(e) => handleDrop(e, folder.id)}
          onClick={() => {
            if (scheduleMode) {
              toggleScheduleSelect(folder.id)
            } else if (renamingId !== folder.id) {
              onNavigate(folder.id)
            }
          }}
        >
          {scheduleMode ? (
            <div className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-md border mr-1.5 transition-all duration-150',
              isScheduleChecked ? 'border-primary bg-primary shadow-sm' : 'border-muted-foreground/30',
            )}>
              {isScheduleChecked && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
            </div>
          ) : (
            <>
              {hasChildren ? (
                <button
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md hover:bg-muted/80 transition-colors"
                  onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id) }}
                >
                  {isExpanded
                    ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    : <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  }
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
            </>
          )}

          {isExpanded && hasChildren ? (
            <FolderOpen className={cn('h-3.5 w-3.5 shrink-0 mr-1.5 transition-colors', isSelected ? 'text-primary' : 'text-muted-foreground')} />
          ) : (
            <Folder className={cn('h-3.5 w-3.5 shrink-0 mr-1.5 transition-colors', isSelected ? 'text-primary' : 'text-muted-foreground')} />
          )}

          {renamingId === folder.id ? (
            <div className="flex flex-1 items-center gap-0.5 min-w-0" onClick={(e) => e.stopPropagation()}>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="h-5 flex-1 text-[11px] px-1.5 min-w-0 rounded-md"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename(folder.id)
                  if (e.key === 'Escape') setRenamingId(null)
                }}
              />
              <Button size="icon" variant="ghost" className="h-5 w-5 rounded-md" onClick={() => handleRename(folder.id)}>
                <Check className="h-2.5 w-2.5 text-primary" />
              </Button>
            </div>
          ) : (
            <>
              <span className="truncate flex-1 min-w-0">{folder.name}</span>

              {/* Tag badges (max 2) */}
              {folder.tags && folder.tags.length > 0 && (
                <span className="hidden sm:flex items-center gap-0.5 shrink-0 mr-1">
                  {folder.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="text-[8px] bg-primary/10 text-primary/80 px-1.5 py-0.5 rounded-full leading-tight font-medium">
                      {tag}
                    </span>
                  ))}
                  {folder.tags.length > 2 && (
                    <span className="text-[8px] text-muted-foreground">+{folder.tags.length - 2}</span>
                  )}
                </span>
              )}

              <span className="text-[10px] text-muted-foreground shrink-0 mr-0.5 tabular-nums font-medium">
                {folder.images.length > 0 ? folder.images.length : ''}
              </span>

              {/* Hover actions */}
              {!scheduleMode && (
                <div className="hidden shrink-0 items-center gap-0.5 group-hover:flex" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                    onClick={async () => {
                      toast.info(t('Preparando ZIP...'))
                      try {
                        const res = await fetch(`${BASE}/media-folders/${folder.id}/download`, {
                          headers: getAuthHeaders(),
                        })
                        if (!res.ok) {
                          const body = await res.json().catch(() => ({ error: 'Erro' }))
                          toast.error(body.error || 'Erro ao baixar')
                          return
                        }
                        const blob = await res.blob()
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `${folder.name}.zip`
                        a.click()
                        URL.revokeObjectURL(url)
                      } catch {
                        toast.error(t('Erro ao baixar pasta'))
                      }
                    }}
                    title={t('Baixar pasta como ZIP')}
                  >
                    <Download className="h-2.5 w-2.5" />
                  </button>
                  <button
                    className="flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                    onClick={() => { setRenamingId(folder.id); setRenameValue(folder.name) }}
                  >
                    <Pencil className="h-2.5 w-2.5" />
                  </button>
                  <button
                    className="flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    onClick={(e) => handleDelete(e, folder.id, folder.name)}
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {isExpanded && children.map(child => renderTreeItem(child, depth + 1))}
      </div>
    )
  }

  function renderFlatItem(folder: MediaFolder) {
    const isSelected = folder.id === currentFolderId
    const isDragOver = folder.id === dragOverId
    const parentPath = getParentPath(folder)

    return (
      <div
        key={folder.id}
        className={cn(
          'group flex items-center h-9 cursor-pointer text-[12px] px-3 mx-1.5 my-0.5 rounded-lg transition-all duration-150',
          isSelected && 'bg-primary/10 text-primary font-medium',
          isDragOver && 'bg-primary/15 ring-1 ring-inset ring-primary/40',
          !isSelected && !isDragOver && 'hover:bg-muted/60',
        )}
        onClick={() => {
          onNavigate(folder.id)
          const ancestors: string[] = []
          let id: string | null | undefined = folder.parentId
          while (id) {
            ancestors.push(id)
            const parent = allFolders.find(f => f.id === id)
            if (!parent || !parent.parentId) break
            id = parent.parentId
          }
          if (ancestors.length > 0) {
            setExpanded(prev => {
              const next = new Set(prev)
              ancestors.forEach(a => next.add(a))
              return next
            })
          }
        }}
        onDragOver={(e) => handleDragOver(e, folder.id)}
        onDragLeave={() => setDragOverId(null)}
        onDrop={(e) => handleDrop(e, folder.id)}
      >
        <FolderOpen className={cn('h-3.5 w-3.5 shrink-0 mr-2', isSelected ? 'text-primary' : 'text-muted-foreground')} />
        <div className="flex-1 min-w-0">
          <span className="truncate block">{folder.name}</span>
          {parentPath && (
            <span className="text-[9px] text-muted-foreground truncate block leading-tight">em {parentPath}</span>
          )}
        </div>
        {folder.tags && folder.tags.length > 0 && (
          <span className="flex items-center gap-0.5 shrink-0 mr-1">
            {folder.tags.slice(0, 2).map(tag => (
              <span key={tag} className="text-[8px] bg-primary/10 text-primary/80 px-1.5 py-0.5 rounded-full leading-tight font-medium">
                {tag}
              </span>
            ))}
          </span>
        )}
        <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums font-medium">
          {folder.images.length > 0 ? folder.images.length : ''}
        </span>
      </div>
    )
  }

  return (
    <div className="flex h-full w-64 shrink-0 flex-col border-r border-border/40">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-border/40">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t('Pastas')}</span>
        <div className="flex items-center gap-0.5">
          {onScheduleFolders && !scheduleMode && allFolders.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-lg"
              onClick={() => {
                setScheduleMode(true)
                setExpanded(new Set(allFolders.map(f => f.id)))
              }}
              title={t('Agendar pastas')}
            >
              <CalendarPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          {scheduleMode ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-lg"
              onClick={exitScheduleMode}
              title={t('Cancelar')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-lg"
              onClick={() => setShowCreate(true)}
              title={currentFolderId ? t('Nova subpasta') : t('Nova pasta')}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Search + tag filter */}
      {allFolders.length > 3 && !scheduleMode && (
        <div className="border-b border-border/40 px-3 py-2 space-y-1.5">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('Buscar pasta...')}
              className="h-7 pl-7 text-[11px] rounded-lg bg-muted/40 border-transparent focus:border-primary/30 focus:bg-transparent transition-colors"
            />
            {searchQuery && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <button
                className={cn(
                  'text-[9px] px-2 py-0.5 rounded-full transition-all duration-150 font-medium',
                  tagFilter === 'all'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
                onClick={() => setTagFilter('all')}
              >
                {t('Todas')}
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  className={cn(
                    'text-[9px] px-2 py-0.5 rounded-full border transition-all duration-150 font-medium',
                    tagFilter === tag
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'border-border/40 text-muted-foreground hover:text-foreground hover:border-border',
                  )}
                  onClick={() => setTagFilter(tagFilter === tag ? 'all' : tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create folder form */}
      {showCreate && (
        <div className="border-b border-border/40 px-3 py-3 space-y-2 bg-muted/20">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={currentFolderId ? t('Nome da subpasta...') : t('Nome da pasta...')}
            className="h-8 text-xs rounded-lg"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
              if (e.key === 'Escape') { setShowCreate(false); setNewName(''); setNewTags('') }
            }}
          />
          <Input
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            placeholder={t('Tags (separar por virgula)...')}
            className="h-7 text-[11px] rounded-lg"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
              if (e.key === 'Escape') { setShowCreate(false); setNewName(''); setNewTags('') }
            }}
          />
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {allTags.slice(0, 6).map(tag => (
                <button
                  key={tag}
                  className="text-[9px] bg-muted/50 hover:bg-muted px-2 py-0.5 rounded-full text-muted-foreground hover:text-foreground transition-colors font-medium"
                  onClick={() => {
                    const currentList = newTags.split(/[,;\s]+/).map(t => t.replace(/^#/, '').trim()).filter(Boolean)
                    if (!currentList.includes(tag)) {
                      setNewTags(prev => prev ? `${prev}, ${tag}` : tag)
                    }
                  }}
                >
                  +{tag}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Button size="sm" className="h-7 flex-1 text-[11px] rounded-lg gap-1" onClick={handleCreate} disabled={createFolder.isPending}>
              <Check className="h-3 w-3" /> {t('Criar')}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] rounded-lg" onClick={() => { setShowCreate(false); setNewName(''); setNewTags('') }}>
              {t('Cancelar')}
            </Button>
          </div>
        </div>
      )}

      {/* Tree / Filtered list */}
      <div
        className="flex-1 overflow-y-auto py-1.5"
        onDragOver={(e) => { e.preventDefault(); setDragOverId('__root__') }}
        onDragLeave={() => setDragOverId(null)}
        onDrop={handleRootDrop}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isFiltering ? (
          filteredFolders.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-3 py-10 text-center">
              <Search className="h-6 w-6 text-muted-foreground/15" />
              <p className="text-[11px] text-muted-foreground">{t('Nenhuma pasta encontrada')}</p>
            </div>
          ) : (
            filteredFolders.map(folder => renderFlatItem(folder))
          )
        ) : rootFolders.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/40">
              <FolderOpen className="h-7 w-7 text-muted-foreground/40" />
            </div>
            <p className="text-[12px] text-muted-foreground">{t('Nenhuma pasta')}</p>
            <Button size="sm" variant="outline" className="gap-1 text-[11px] h-7 rounded-lg" onClick={() => setShowCreate(true)}>
              <Plus className="h-3 w-3" /> {t('Criar pasta')}
            </Button>
          </div>
        ) : (
          rootFolders.map(folder => renderTreeItem(folder, 0))
        )}
      </div>

      {/* Schedule action bar */}
      {scheduleMode && scheduleSelected.size > 0 ? (
        <div className="border-t border-border/40 px-3 py-3">
          <Button
            size="sm"
            className="w-full gap-1.5 text-xs rounded-xl"
            onClick={handleScheduleSelected}
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            {t('Agendar')} {scheduleSelected.size} {scheduleSelected.size !== 1 ? t('pastas') : t('pasta')}
          </Button>
        </div>
      ) : allFolders.length > 0 && (
        <div className="border-t border-border/40 px-4 py-2.5">
          <p className="text-[10px] text-muted-foreground font-medium">
            {isFiltering ? `${filteredFolders.length} ${t('resultados')}` : (
              <>
                {allFolders.length} {allFolders.length !== 1 ? t('pastas') : t('pasta')} &middot;{' '}
                {allFolders.reduce((sum, f) => sum + f.images.length, 0)} {t('arquivos')}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  )
}
